/**
 * Session Ended Domain
 * The session's end: the notice over the game when the server ends the
 * session — its end lobby run out, or its world reset or deleted while it was
 * played — and, once the player has left it, the entry screen's "session
 * ended" state, which joins nothing by itself. Triggered by OpCodeSessionEnded
 * (9), which the server sends everyone in the match before it ends it.
 *
 * The tab remembers the end: kept in this tab's session storage under the
 * session its link names (linkSession, domains/entry-links.ts), so a reload of
 * that link shows the end again and joins nothing, where a student's link
 * would join the ended session again by itself and be refused. A link to
 * another session joins as usual.
 */

import { create } from 'zustand';
import type { MessageType } from '../constants/op-codes';
import { endPlay } from './end-play';
import { linkSession, pageSearch } from './entry-links';

/**
 * OpCodeSessionEnded as the server sends it (match-handler.go), to everyone in
 * the match: the world's name is what the notice says, and all it reads.
 */
export interface SessionEndedMessage {
  type: typeof MessageType.MessageTypeSessionEnded;
  world_name: string;
  /** When it ended, in Unix seconds: not shown. */
  ended_at_unix?: number;
}

/** Where the tab keeps the end of the session its link names. */
const REMEMBERED_END_KEY = 'ponder-flight-game-session-ended';

/** The end this tab remembers: the session the link named, and its world. */
interface RememberedEnd {
  session: string;
  world: string;
}

/** The end this tab remembers for the session its link names, if that session ended. */
function rememberedEnd(): RememberedEnd | null {
  const session = linkSession(pageSearch());
  if (!session) return null;
  try {
    const end = JSON.parse(sessionStorage.getItem(REMEMBERED_END_KEY) ?? 'null') as Partial<RememberedEnd> | null;
    return end?.session === session ? { session, world: typeof end.world === 'string' ? end.world : '' } : null;
  } catch {
    // No storage — a private window, or none outside a browser — or a value that is not ours.
    return null;
  }
}

/** Remember that the session this tab's link names has ended, in this world. */
function rememberEnd(world: string): void {
  const session = linkSession(pageSearch());
  if (!session) return;
  try {
    sessionStorage.setItem(REMEMBERED_END_KEY, JSON.stringify({ session, world } satisfies RememberedEnd));
  } catch {
    // No storage: the end is known until the page reloads.
  }
}

interface SessionEndedState {
  /** The notice is open (domains/notices.ts draws one notice at a time). */
  noticeOpen: boolean;
  /** The world whose session ended, as the server names it. */
  worldName: string;
  /**
   * The player has left the ended session, or the tab remembers its end: the
   * entry screen says the session ended and joins nothing by itself.
   */
  left: boolean;
  /** Opens the notice for the session played in this world. */
  show: (worldName: string) => void;
  /**
   * Leaving the ended session — the notice's Exit, or Exit or Disconnect in
   * the end lobby, before the server has named the world: the notice goes,
   * and the entry screen will say the session ended. `worldPlayed` names the
   * world when the notice has not.
   */
  leave: (worldPlayed?: string) => void;
  /** Back to how a page starts: no notice, and the entry screen says the end only if the tab remembers it. */
  reset: () => void;
}

/** How a page starts: the end the tab remembers for its link, or none. */
function pageStart() {
  const end = rememberedEnd();
  return { noticeOpen: false, worldName: end?.world ?? '', left: end !== null };
}

export const useSessionEndedStore = create<SessionEndedState>((set, get) => ({
  ...pageStart(),
  show: (worldName) => {
    rememberEnd(worldName);
    set({ noticeOpen: true, worldName });
  },
  leave: (worldPlayed) => {
    const worldName = get().worldName || worldPlayed || '';
    rememberEnd(worldName);
    set({ noticeOpen: false, left: true, worldName });
  },
  reset: () => set(pageStart()),
}));

/**
 * The player leaves the ended session (authService.leaveEndedSession): the
 * notice goes, and the entry screen will say the session ended. `worldPlayed`
 * names the world when the notice has not.
 */
export function handleEndedSessionLeft(worldPlayed?: string): void {
  useSessionEndedStore.getState().leave(worldPlayed);
}

/**
 * Handle OpCodeSessionEnded: the session is over, and the player is thanked.
 * Play ends with it (endPlay): a moderator's call or invitation, and a command
 * on its way — the server ends the match next.
 */
export function handleSessionEnded(data: SessionEndedMessage): void {
  endPlay();
  useSessionEndedStore.getState().show(data.world_name);
}
